import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Delete, PersonAdd } from "@mui/icons-material";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/auth";

const ROLE_HIERARCHY = { owner: 3, manager: 2, staff: 1 };

const ROLE_COLORS = {
  owner: "error",
  manager: "warning",
  staff: "default",
};

const ROLE_LABELS = {
  owner: "Owner",
  manager: "Manager",
  staff: "Staff",
};

export default function StaffManagement({ bizId }) {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUid, setCurrentUid] = useState(null);

  // Add staff form
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("staff");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const db = firebase.firestore();

  // Load staff list
  useEffect(() => {
    if (!bizId) return;
    const unsubscribe = db
      .collection("Business")
      .doc(bizId)
      .collection("Staff")
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snapshot) => {
          const list = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setStaffList(list);
          setLoading(false);
        },
        (err) => {
          console.error("Staff load error:", err);
          setLoading(false);
        }
      );
    return () => unsubscribe();
  }, [bizId]);

  // Determine current user's role
  useEffect(() => {
    const user = firebase.auth().currentUser;
    if (!user || !bizId) return;
    setCurrentUid(user.uid);

    // Check if user is in staff list
    const unsubscribe = db
      .collection("Business")
      .doc(bizId)
      .collection("Staff")
      .where("uid", "==", user.uid)
      .onSnapshot((snapshot) => {
        if (!snapshot.empty) {
          setCurrentUserRole(snapshot.docs[0].data().role);
        } else {
          // Check if user is the business creator (legacy owner)
          // or default to owner for backward compatibility
          db.collection("Business")
            .doc(bizId)
            .get()
            .then((doc) => {
              // Auto-add as owner — either legacy Business.uid match or
              // backward-compatible default (user has page access but no
              // Staff doc yet)
              db.collection("Business")
                .doc(bizId)
                .collection("Staff")
                .add({
                  uid: user.uid,
                  email: user.email,
                  role: "owner",
                  addedBy: user.uid,
                  createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                })
                .then(() => setCurrentUserRole("owner"));
            });
        }
      });
    return () => unsubscribe();
  }, [bizId]);

  const canAssignRole = (targetRole) => {
    if (!currentUserRole) return false;
    const myLevel = ROLE_HIERARCHY[currentUserRole] || 0;
    const targetLevel = ROLE_HIERARCHY[targetRole] || 0;
    // Can only assign roles below your own level
    // Exception: owners can assign other owners
    if (currentUserRole === "owner") return true;
    return myLevel > targetLevel;
  };

  const canDeleteMember = (member) => {
    if (!currentUserRole || !currentUid) return false;
    // Can't delete yourself
    if (member.uid === currentUid) return false;
    const myLevel = ROLE_HIERARCHY[currentUserRole] || 0;
    const memberLevel = ROLE_HIERARCHY[member.role] || 0;
    // Can only delete members with lower role level
    // Owners can delete anyone except themselves
    if (currentUserRole === "owner") return true;
    return myLevel > memberLevel;
  };

  const getAssignableRoles = () => {
    if (currentUserRole === "owner") return ["owner", "manager", "staff"];
    if (currentUserRole === "manager") return ["staff"];
    return [];
  };

  const handleAddStaff = async () => {
    setError("");
    setSuccess("");
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError("Please enter an email address.");
      return;
    }

    if (!canAssignRole(role)) {
      setError("You don't have permission to assign this role.");
      return;
    }

    // Check if email already exists in staff
    const existing = staffList.find(
      (s) => s.email.toLowerCase() === trimmedEmail
    );
    if (existing) {
      setError("This email is already in the staff list.");
      return;
    }

    setAdding(true);
    try {
      const user = firebase.auth().currentUser;
      await db
        .collection("Business")
        .doc(bizId)
        .collection("Staff")
        .add({
          email: trimmedEmail,
          role: role,
          uid: null, // Will be linked when the user logs in
          addedBy: user.uid,
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      setEmail("");
      setRole("staff");
      setSuccess(`${trimmedEmail} added as ${ROLE_LABELS[role]}.`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
    setAdding(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await db
        .collection("Business")
        .doc(bizId)
        .collection("Staff")
        .doc(deleteTarget.id)
        .delete();
      setSuccess(`${deleteTarget.email} removed from staff.`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const assignableRoles = getAssignableRoles();
  const canAdd = assignableRoles.length > 0;

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" gutterBottom>
        Staff Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage who has access to this venue. Owners can assign any role.
        Managers can add or remove Staff only. Staff can view everything but
        cannot add/delete anything.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Add staff form */}
      {canAdd && (
        <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
          <Typography variant="subtitle2" gutterBottom>
            Add Staff Member
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
            <TextField
              label="Email Address"
              type="email"
              size="small"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ minWidth: 280 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={role}
                label="Role"
                onChange={(e) => setRole(e.target.value)}
              >
                {assignableRoles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={handleAddStaff}
              disabled={adding || !email.trim()}
            >
              {adding ? "Adding..." : "Add"}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Staff list */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staffList.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No staff members yet. Add your first team member above.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {staffList.map((member) => (
              <TableRow key={member.id}>
                <TableCell>
                  {member.email}
                  {member.uid === currentUid && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (you)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={ROLE_LABELS[member.role] || member.role}
                    color={ROLE_COLORS[member.role] || "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.uid ? "Linked" : "Pending"}
                    variant={member.uid ? "filled" : "outlined"}
                    color={member.uid ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  {canDeleteMember(member) && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(member)}
                      aria-label={`Remove ${member.email}`}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Remove Staff Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{" "}
            <strong>{deleteTarget?.email}</strong> ({ROLE_LABELS[deleteTarget?.role]}) from this venue?
            They will no longer have access.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
